/**
 * ConvLinkPage — public magic-link redeem page (iter124).
 *
 * Mounted at `/conv-link/:token` (no auth required). External users
 * who received an emailed invite land here, type the OTP from their
 * inbox, and on success the page mints a session and redirects them
 * to /conversations-v2 with the session stored in localStorage.
 *
 * Security notes:
 *  • Token is in the URL path; OTP is typed manually so it can't leak
 *    through email-trackers / link-previewers.
 *  • Backend caps wrong-OTP attempts at 5; this UI surfaces the
 *    remaining-attempts count in the inline error so users know
 *    when they're getting close.
 */
import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ChatCircle, ArrowRight, Warning, CheckCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";

const SUPPORTED_LANGS = new Set(["en", "fr", "es"]);

/** Browser-language detect for first-time external invitees.
 *  Returns null when nav.language isn't in the supported set so we
 *  don't override an explicit choice with a noisy default. */
function detectBrowserLang() {
  try {
    const raw = (typeof navigator !== "undefined" && navigator.language) || "";
    const code = raw.slice(0, 2).toLowerCase();
    return SUPPORTED_LANGS.has(code) ? code : null;
  } catch {
    return null;
  }
}

export default function ConvLinkPage() {
  const { t, lang, setLang } = useI18n();
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // If the user already has a valid session for this device, skip
  // straight to the chat. (Handy if they re-open the email a second
  // time and don't want to re-OTP.)
  useEffect(() => {
    const existing = localStorage.getItem("usair_conv_session");
    if (!existing) return;
    api.get("/conv/me").then(() => navigate("/conversations-v2"))
      .catch(() => { /* fall through to OTP form */ });
  }, [navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^\d{6}$/.test(otp.trim())) {
      setError(t("convlink.error_enter_code", "Enter the 6-digit code from your email."));
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/conv/auth/redeem", {
        token,
        otp: otp.trim(),
      });
      localStorage.setItem("usair_conv_session", r.data.session);
      setSuccess(true);
      toast.success(t("convlink.toast_signed_in", "Signed in — redirecting…"));
      // Clear localStorage usair_user since this is an external session
      // (a Person, not an OAuth admin). The chat UI reads /conv/me
      // for identity instead.
      try { localStorage.removeItem("usair_user"); } catch { /* ignore */ }
      // iter139.5 — language autodetect+persist on first redeem.
      // (1) Ask the server if this person already has a saved language
      //     (e.g. they OTP'd from a different device last week).
      // (2) Fall back to the browser language if no preference exists.
      // The persisted choice ALWAYS wins so language follows the person,
      // not the device.
      try {
        const me = await api.get("/conv/me/lang");
        const saved = (me.data?.lang || "").toLowerCase();
        const detected = detectBrowserLang();
        const pick = SUPPORTED_LANGS.has(saved) ? saved : detected;
        if (pick && pick !== lang) setLang(pick);
        if (!saved && detected && detected !== "en") {
          // Best-effort persist; fire-and-forget so a slow network
          // can't block the redirect to the chat.
          api.put("/conv/me/lang", { lang: detected }).catch(() => {});
        }
      } catch {
        // Auth/network glitch — non-fatal, the chat will still open.
      }
      setTimeout(() => navigate("/conversations-v2"), 700);
    } catch (e2) {
      setError(e2?.response?.data?.detail || t("convlink.error_redeem_failed", "Could not redeem this invite."));
    } finally { setBusy(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="text-center">
          <Warning size={36} className="mx-auto text-amber-500 mb-3" />
          <h1 className="text-lg font-black mb-1">{t("convlink.missing_token_title", "Missing invitation token")}</h1>
          <p className="text-sm text-slate-500">{t("convlink.missing_token_subtitle", "Please open the link from your email exactly as it was sent.")}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-slate-50 flex items-center justify-center p-4"
      data-testid="conv-link-page"
    >
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-sm shadow-sm p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-sm bg-blue-600 flex items-center justify-center text-white shrink-0">
            <ChatCircle size={20} weight="fill" />
          </div>
          <div>
            <h1 className="text-base font-black tracking-tight font-['Chivo']">{t("convlink.title", "Open your Conversation")}</h1>
            <p className="text-xs text-slate-500">{t("convlink.subtitle", "Enter the 6-digit code from your email.")}</p>
          </div>
        </div>
        {success ? (
          <div className="text-center py-6" data-testid="conv-link-success">
            <CheckCircle size={36} className="mx-auto text-emerald-600 mb-2" />
            <div className="text-sm font-bold text-slate-900">{t("convlink.success_title", "You're in.")}</div>
            <div className="text-xs text-slate-500 mt-1">{t("convlink.success_subtitle", "Redirecting to your conversation…")}</div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-3">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1">{t("convlink.label_otp", "One-time code")}</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/[^\d]/g, "").slice(0, 6))}
                placeholder="123456"
                autoFocus
                data-testid="conv-link-otp-input"
                className="w-full text-center text-lg tracking-[0.4em] font-mono font-bold py-3 rounded-sm border border-slate-300 focus:border-blue-500 focus:outline-none"
              />
            </div>
            {error && (
              <div
                data-testid="conv-link-error"
                className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-sm px-3 py-2"
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={busy || otp.length !== 6}
              data-testid="conv-link-submit-btn"
              className="w-full h-10 flex items-center justify-center gap-1.5 rounded-sm bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 text-xs font-bold uppercase tracking-wider"
            >
              {busy ? t("convlink.btn_verifying", "Verifying…") : t("convlink.btn_submit", "Open Conversation")}
              <ArrowRight size={14} weight="bold" />
            </button>
          </form>
        )}
        <div className="mt-6 pt-4 border-t border-slate-100 text-center text-[11px] text-slate-400">
          {t("convlink.footer_expires", "The link expires 7 days after it was sent.")}
          {" "}
          <Link to="/" className="text-blue-700 hover:underline">{t("convlink.footer_back", "Back to portal")}</Link>
        </div>
      </div>
    </div>
  );
}
